import { cx } from '@emotion/css';
import type { Profile } from '@gm-commercial/profile-model';
import { Box, Button, Toolbar } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_PROFILE_PERSONAL_PATH } from '~/common/models/pages/accountPages.model';
import { SETTINGS_VIEW_PATH } from '~/common/models/pages/settingsPages.model';
import { SUPPORT_FAQS_PATH } from '~/common/models/pages/supportPages.model';
import ProfileMenu from '~/features/header/components/ProfileMenu/ProfileMenu';
import { getDefaultProfileMenu } from '~/features/header/components/ProfileMenu/ProfileMenu.config';
import { StyledAppBar } from '~/features/header/GlobalHeader';
import useStyles from '~/features/header/GlobalHeader.styles';

/** Reuses advisor header presentation without unsupported search or notification hooks. */
const GmfAdvisorHeader = ({
  navExpanded,
  profile,
}: {
  navExpanded: boolean;
  profile: Profile;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <StyledAppBar
      className={classes.headerWrapper}
      position="fixed"
      open={navExpanded}
    >
      <Box className={classes.headerInner}>
        <Box className={classes.container}>
          <Toolbar className={classes.header}>
            <Box>
              <Button
                id="user-menu-btn"
                aria-label={t('header.openUserMenu')}
                aria-controls={anchorEl ? 'menu' : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(anchorEl)}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                className={classes.profileContainerButton}
              >
                <Box
                  className={cx(classes.profileContainer, {
                    isOpen: Boolean(anchorEl),
                  })}
                >
                  {profile.firstName?.charAt(0)}
                  {profile.lastName?.charAt(0)}
                </Box>
              </Button>
              <ProfileMenu
                label={t('header.profileMenu')}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                profile={profile}
                items={[
                  {
                    label: 'common:header.profile',
                    testId: 'account-profile-global-nav',
                    onClick: { redirect: ACCOUNT_PROFILE_PERSONAL_PATH },
                    section: 'top',
                  },
                  {
                    label: 'common:header.settings',
                    testId: 'account-settings-global-nav',
                    onClick: { redirect: SETTINGS_VIEW_PATH },
                    section: 'top',
                  },
                  {
                    label: 'common:header.support',
                    testId: 'account-support-global-menu',
                    onClick: { redirect: SUPPORT_FAQS_PATH },
                    section: 'top',
                  },
                  ...getDefaultProfileMenu(),
                ].map((item) => ({
                  ...item,
                  label: t(item.label),
                }))}
              />
            </Box>
          </Toolbar>
        </Box>
      </Box>
    </StyledAppBar>
  );
};

export default GmfAdvisorHeader;
